import { useState, useCallback, useEffect } from "react";
import {
  validateNormalReservationSettingsForm,
  validateNormalReservationTimeSlotSettingsForm,
} from "../../validations/normalReservationSettingsForm";
import { useToastPromise } from "../../components/toastPromise";
import { CollaborationController } from "../../controllers/collaborationController";
import { Dropdown } from "../../components/dropdown";
import Button from "@mui/material/Button";
import "../../assets/css/setttings.css";
import { CollapsibleContainer } from "../../components/collapsibleContainer";
import { Textbox } from "../../components/textbox";
import { SlotStatusList } from "../../constants/slotStatusList";
import { TableList } from "../../components/tableList";
import { ReservationSettingsController } from "../../controllers/reservationSettingsController";
import { RichText } from "../../components/richText";
import { TableName } from "../../constants/tableName";
import { RichTextPreview } from "../../components/richTextPreview";

export const NormalReservationSettings = ({ user }) => {
  const toastPromise = useToastPromise();
  const emptyHTMLString = "<p>&nbsp;</p>";
  const initialNormalTimeSlot = {
    id: "",
    slotTitle: "",
    slotStatus: "",
    maxPax: 0,
    startTime: "",
    endTime: "",
  };
  const [normalTimeSlot, setNormalTimeSlot] = useState(initialNormalTimeSlot);
  const [timeSlotsList, setTimeSlotsList] = useState([]);
  const [normalErrors, setNormalErrors] = useState({});
  const [normalIdCounter, setNormalIdCounter] = useState(1);
  const initialNormal = {
    timeSlotsList: [],
    maxPaxSelection: 0,
    reservationDescription: emptyHTMLString,
  };
  const initialInputs = {
    collaborationId: "",
    normalSettings: initialNormal,
    generalDescription: emptyHTMLString,
  };
  const [inputs, setInputs] = useState(initialInputs);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [collaborations, setCollaborations] = useState([]);
  const [collaborationList, setCollaborationList] = useState([]);
  const [
    isNormalReservationDescriptionPreviewOpen,
    setIsNormalReservationDescriptionPreviewOpen,
  ] = useState(false);
  const [
    isNormalReservationDescriptionPreviewDisabled,
    setIsNormalReservationDescriptionPreviewDisabled,
  ] = useState(true);
  const [isGeneralDescriptionPreviewOpen, setIsGeneralDescriptionPreviewOpen] =
    useState(false);
  const [
    isGeneralDescriptionPreviewDisabled,
    setIsGeneralDescriptionPreviewDisabled,
  ] = useState(true);
  const getLargestId = (slots) => {
    return slots.reduce((max, slot) => Math.max(max, slot.id), 0);
  };
  const resetForm = () => {
    setInputs(initialInputs);
    setErrors({});
    setNormalTimeSlot(initialNormalTimeSlot);
    setTimeSlotsList([]);
    setNormalErrors({});
    setNormalIdCounter(1);
    setIsNormalReservationDescriptionPreviewDisabled(true);
    setIsGeneralDescriptionPreviewDisabled(true);
  };
  const handleSubmit = (event) => {
    event.preventDefault();
    const newErrors = validateNormalReservationSettingsForm(inputs);
    setErrors(newErrors);
    if (Object.keys(newErrors).length !== 0) {
      console.debug("Form submission failed due to validation errors.");
      return;
    }
    console.debug("Form validated successfully.");
    saveReservationSettings();
  };
  const handleSubmitNormal = (event) => {
    event.preventDefault();
    const newErrors =
      validateNormalReservationTimeSlotSettingsForm(normalTimeSlot);
    setNormalErrors(newErrors);
    if (Object.keys(newErrors).length !== 0) {
      console.debug("Form submission failed due to validation errors.");
      return;
    }
    console.debug("Form validated successfully.");
    saveReservationSettingsNormal();
  };
  const saveReservationSettings = () => {
    setLoading(true);
    toastPromise({
      promise: new Promise((resolve, reject) => {
        inputs.id
          ? ReservationSettingsController.update({
              parameters: {
                ...inputs,
                id: inputs.id,
                lastModifiedBy: user.uid,
                lastModifiedDate: new Date().toLocaleString(),
              },
              resolve: resolve,
              reject: reject,
            })
          : ReservationSettingsController.add({
              parameters: {
                ...inputs,
                createdBy: user.uid,
                createdDate: new Date().toLocaleString(),
                lastModifiedBy: user.uid,
                lastModifiedDate: new Date().toLocaleString(),
              },
              resolve: resolve,
              reject: reject,
            });
      }),
      successMessage: (data) => {
        console.debug(data);
        return data;
      },
      errorMessage: (data) => data,
    }).finally(() => {
      setLoading(false);
      resetForm();
      getAll();
    });
  };
  const saveReservationSettingsNormal = () => {
    setTimeSlotsList((prevTimeSlots) => [
      ...prevTimeSlots,
      {
        ...normalTimeSlot,
        id: normalIdCounter,
      },
    ]);
    setNormalTimeSlot({ ...initialNormalTimeSlot, id: normalIdCounter + 1 });
    setNormalErrors({});
    setNormalIdCounter(normalIdCounter + 1);
  };
  const onHandleChange = useCallback((value, name) => {
    setInputs((values) => ({ ...values, [name]: value }));
  }, []);
  const handleTextChange = (event) => {
    saveInputs(event.target.name, event.target.value);
  };
  const handleRichTextChange = (event, editor) => {
    const data = editor.getData();
    if (editor.config.get("name") === "generalDescription") {
      setInputs((values) => ({ ...values, [editor.config.get("name")]: data }));
      setIsGeneralDescriptionPreviewDisabled(data?.trim() === emptyHTMLString);
    } else saveInputs(editor.config.get("name"), data);
    if (editor.config.get("name") === "reservationDescription")
      setIsNormalReservationDescriptionPreviewDisabled(
        data?.trim() === emptyHTMLString
      );
  };
  const saveInputs = (name, value) => {
    setInputs((prevInputs) => ({
      ...prevInputs,
      normalSettings: {
        ...prevInputs.normalSettings,
        [name]: value,
      },
    }));
  };
  const onHandleNormalChange = useCallback((value, name) => {
    saveNormalInputs(name, value);
  }, []);
  const handleNormalTextChange = (event) => {
    saveNormalInputs(event.target.name, event.target.value);
  };
  const saveNormalInputs = (name, value) => {
    setNormalTimeSlot((values) => ({ ...values, [name]: value }));
  };
  const getAllCollaborations = () => {
    setLoading(true);
    toastPromise({
      promise: new Promise((resolve, reject) => {
        CollaborationController.getAll({
          resolve: resolve,
          reject: reject,
        });
      }),
      successMessage: (data) => {
        setCollaborations(
          data.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }))
        );
        setCollaborationList(
          data.docs.map((doc) => ({
            label: doc.data().title,
            value: doc.id,
          }))
        );
        return "Collaboration details retrieved successfully.";
      },
      errorMessage: (data) => data,
    }).finally(() => setLoading(false));
  };
  const getAll = () => {
    setLoading(true);
    toastPromise({
      promise: new Promise((resolve, reject) => {
        ReservationSettingsController.getAll({
          resolve: resolve,
          reject: reject,
        });
      }),
      successMessage: (data) => {
        if (data) {
          const fetchedData = data.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));
          if (fetchedData.length > 0) {
            setInputs(fetchedData[0]);
            const timeSlotsList =
              fetchedData[0]?.normalSettings?.timeSlotsList || [];
            if (timeSlotsList.length > 0) {
              setTimeSlotsList(timeSlotsList);
              setNormalIdCounter(getLargestId(timeSlotsList) + 1);
            }
            setIsNormalReservationDescriptionPreviewDisabled(
              fetchedData[0]?.normalSettings?.reservationDescription === "" ||
                fetchedData[0]?.normalSettings?.reservationDescription?.trim() ===
                  emptyHTMLString
            );
            setIsGeneralDescriptionPreviewDisabled(
              fetchedData[0]?.generalDescription === "" ||
                fetchedData[0]?.generalDescription?.trim() === emptyHTMLString
            );
          }
        }
        return "Reservation settings details retrieved successfully.";
      },
      errorMessage: (data) => data,
    }).finally(() => setLoading(false));
  };
  useEffect(() => {
    getAllCollaborations();
    getAll();
  }, []);
  useEffect(() => {
    setInputs((prevInputs) => ({
      ...prevInputs,
      normalSettings: {
        ...prevInputs.normalSettings,
        timeSlotsList: timeSlotsList,
      },
    }));
  }, [timeSlotsList]);

  return (
    <section id="normalReservationSettings">
      <h4>NORMAL RESERVATION SETTINGS</h4>
      <hr />
      <Button
        onClick={handleSubmit}
        disabled={loading || inputs?.normalSettings?.timeSlotsList?.length < 1}
      >
        {loading ? "Saving" : "Save"}
      </Button>
      <form onSubmit={handleSubmit}>
        <Dropdown
          name="collaborationId"
          title="Collaboration"
          value={inputs?.collaborationId}
          options={collaborationList}
          onChangeFunc={onHandleChange}
          isClearable={false}
          placeholder="Collaboration"
          errorMsg={errors.collaborationId}
        />
        <Textbox
          type="text"
          name="collaborationName"
          placeholder="Collaboration Name"
          value={
            collaborations.find((data) => data.id === inputs?.collaborationId)
              ?.title ?? "NA"
          }
          readOnly={true}
        />
        <Textbox
          type="text"
          name="startDate"
          placeholder="Start Date"
          value={
            collaborations.find((data) => data.id === inputs?.collaborationId)
              ?.startDate ?? "NA"
          }
          readOnly={true}
        />
        <Textbox
          type="text"
          name="endDate"
          placeholder="End Date"
          value={
            collaborations.find((data) => data.id === inputs?.collaborationId)
              ?.endDate ?? "NA"
          }
          readOnly={true}
        />
        <Textbox
          type="number"
          name="maxPaxSelection"
          placeholder="Max Pax Selection"
          value={inputs?.normalSettings?.maxPaxSelection}
          onChange={handleTextChange}
          errorMsg={errors.maxPaxSelection}
        />
        <RichText
          name="generalDescription"
          onChange={handleRichTextChange}
          placeholder="General Description"
          data={inputs?.generalDescription || emptyHTMLString}
          errorMsg={errors.generalDescription}
          tableName={TableName.RESERVATIONSETTINGS}
        />
        <Button
          onClick={() => setIsGeneralDescriptionPreviewOpen(true)}
          disabled={isGeneralDescriptionPreviewDisabled}
        >
          Preview
        </Button>
        <RichTextPreview
          isPreviewOpen={isGeneralDescriptionPreviewOpen}
          handlePreviewClose={() => setIsGeneralDescriptionPreviewOpen(false)}
          content={inputs?.generalDescription}
        />
        <RichText
          name="reservationDescription"
          onChange={handleRichTextChange}
          placeholder="Reservation Description"
          data={
            inputs?.normalSettings?.reservationDescription || emptyHTMLString
          }
          errorMsg={errors.reservationDescription}
          tableName={TableName.RESERVATIONSETTINGS}
        />
        <Button
          onClick={() => setIsNormalReservationDescriptionPreviewOpen(true)}
          disabled={isNormalReservationDescriptionPreviewDisabled}
        >
          Preview
        </Button>
        <RichTextPreview
          isPreviewOpen={isNormalReservationDescriptionPreviewOpen}
          handlePreviewClose={() =>
            setIsNormalReservationDescriptionPreviewOpen(false)
          }
          content={inputs?.normalSettings?.reservationDescription}
        />
        <hr />
        <div>
          <CollapsibleContainer
            title="Normal Slots"
            setCollapsed={timeSlotsList?.length > 0}
            element={
              <div>
                <Button onClick={handleSubmitNormal} disabled={loading}>
                  {loading ? "Adding" : "Add"}
                </Button>
                <Textbox
                  type="text"
                  name="slotTitle"
                  placeholder="Slot Title"
                  value={normalTimeSlot.slotTitle}
                  onChange={handleNormalTextChange}
                  errorMsg={normalErrors.slotTitle}
                />
                <Dropdown
                  name="slotStatus"
                  title="Slot Status"
                  value={normalTimeSlot?.slotStatus}
                  options={SlotStatusList}
                  onChangeFunc={onHandleNormalChange}
                  isClearable={false}
                  placeholder="Slot Status"
                  errorMsg={normalErrors.slotStatus}
                />
                <Textbox
                  type="number"
                  name="maxPax"
                  placeholder="Max Pax"
                  value={normalTimeSlot.maxPax}
                  onChange={handleNormalTextChange}
                  errorMsg={normalErrors.maxPax}
                />
                <Textbox
                  type="time"
                  name="startTime"
                  placeholder="Start Time"
                  value={normalTimeSlot.startTime}
                  onChange={handleNormalTextChange}
                  errorMsg={normalErrors.startTime}
                />
                <Textbox
                  type="time"
                  name="endTime"
                  placeholder="End Time"
                  value={normalTimeSlot.endTime}
                  onChange={handleNormalTextChange}
                  errorMsg={normalErrors.endTime}
                />
              </div>
            }
          />
          {timeSlotsList?.length > 0 && (
            <TableList
              loading={loading}
              list={timeSlotsList}
              isPreview={true}
              deletePromise={(selectedRows, success) => {
                setTimeSlotsList((prevList) =>
                  prevList.filter((item) => !selectedRows.includes(item.id))
                );
              }}
            />
          )}
        </div>
      </form>
    </section>
  );
};
